import { IconButton, TextField, Tooltip } from '@material-ui/core';
import { Clear } from '@material-ui/icons';
import cx from 'classnames';
import CheckIcon from 'components/base/icons/CheckIcon';
import { icon_magnifier } from 'components/base/images';
import CustomDatePicker from 'components/common/input/CustomDatePicker';
import MuiWrapper from 'components/common/input/MuiWrapper';
import { validInputString } from 'lib/library';
import { projectProcessFlagList } from 'lib/mapper';
import { useDidUpdateEffect } from 'lib/utils';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallowAppSelector } from 'store/hooks';
import { isClinicUserSelector } from 'store/modules/user';
import styled from 'styled-components';
import { color } from 'styles/utils';

export interface ProjectSearchFilterProps {
  viewType?: string;
  period: { value: Date; onChange: (e: any) => void; setValue: (value: Date) => void };
  checkedProjectProcess: {
    value: Set<number>;
    onChange: (e: any) => void;
    setValue: (value: Set<number>) => void;
  };
  projectType?: { value: number; onChange: (e: any) => void; setValue: (value: number) => void };
  keyword: { value: string; onChange: (e: any) => void; setValue: (value: string) => void };
  isSubmit?: boolean | null;
  onChangePage: (value: number) => void;
  onSearch: () => void;
  onChangeProjectProcess: (id: number) => void;
  onCheckAllStage: (checkAll: boolean) => void;
  onChangeDate: (e: any) => void;
  isPatient?: boolean;
  isOffline?: boolean;
}

const ProjectSearchFilter = ({
  viewType,
  period,
  checkedProjectProcess,
  projectType,
  keyword,
  isSubmit,
  onChangePage,
  onSearch,
  onChangeProjectProcess,
  onCheckAllStage,
  onChangeDate,
  isPatient,
  isOffline = false,
}: ProjectSearchFilterProps) => {
  const { isClinicUser } = useShallowAppSelector(state => ({
    isClinicUser: isClinicUserSelector(state),
  }));
  const { t } = useTranslation();
  const [isCheckAll, setIsCheckAll] = useState<boolean>(true);
  useDidUpdateEffect(() => {
    let checkListLength = 0;
    if (isClinicUser) {
      checkListLength = 7;
    } else {
      checkListLength = 8;
    }
    if (checkedProjectProcess.value.size === checkListLength) {
      setIsCheckAll(true);
    } else {
      setIsCheckAll(false);
    }
  }, [checkedProjectProcess.value, isCheckAll, projectProcessFlagList]);

  // const handleCheckAllStage = (checkAll: boolean) => {
  //   checkedProjectProcess.setValue(new Set([]));
  //   if (!checkAll) {
  //     projectProcessFlagList.forEach((item: any) => {
  //       if (isClinicUser && item.id === -2) return; // urgent
  //       if (item.id === -3) return; // none
  //       if (item.id === 0) return; // new
  //       if (item.id === 4) return; // cam
  //       checkedProjectProcess.onChange({ value: item.id });
  //     });
  //   }
  // };

  const handleClear = () => {
    keyword.setValue('');
  };

  return (
    <StyledProjectSearchFilter className="projectList__filter_box" viewType={viewType}>
      <MuiWrapper
        className="projectList__search_wrapper"
        config={{
          borderColor: 'transparent',
          activeBorderColor: 'transparent',
          hoverBorderColor: 'transparent',
        }}
      >
        <div className="projectList__search_container">
          <TextField
            rows={1}
            className="radius-md projectList__search_bar"
            variant="outlined"
            fullWidth
            inputProps={{
              fontSize: 13,
              fontWeight: 400,
            }}
            placeholder={t('SEACH_FOR_PROJECT')}
            value={keyword.value || ''}
            onChange={e => {
              const checkText = validInputString(e.target.value);
              keyword.onChange({ value: checkText });
            }}
            onKeyPress={e => e.key === 'Enter' && onSearch()}
            InputProps={{
              endAdornment: (
                <IconButton onClick={handleClear} edge="end" disabled={isOffline}>
                  <Clear />
                </IconButton>
              ),
            }}
          />
          <button className={cx('btn-reset projectList__search_btn')} onClick={onSearch}>
            <img src={icon_magnifier} alt="search" />
          </button>
        </div>
      </MuiWrapper>
      <Tooltip
        className="projectInformation__offline_tooltip"
        title={t('NOT_USE_OFFLINE')}
        placement="top"
        disableHoverListener={!isOffline}
        disableFocusListener={true}
        disableTouchListener={true}
      >
        <div className="projectList__filter_step_box">
          <CustomDatePicker
            type="rangeDate"
            fullWidth
            borderColor="#B5B7C1"
            height={40}
            value={period.value}
            onChange={(e: any) => {
              onChangeDate(e);
              period.onChange(e);
            }}
            className={cx({
              error: isSubmit ? !period.value : false,
            })}
            isClient={true}
            disabled={isOffline}
          />
          <ul className="projectList__filter_step_list">
            <li
              className={cx(`projectList__filter_step_item`, {
                active: isCheckAll,
                offline: isOffline,
              })}
              onClick={() => {
                if (isOffline) return;

                onCheckAllStage(isCheckAll);
                setIsCheckAll(!isCheckAll);
              }}
            >
              <span
                className={`projectList__filter_step_check`}
                style={{
                  borderColor: isCheckAll ? 'transparent' : '#ccc',
                  backgroundColor: isCheckAll ? (isOffline ? '#ccc' : '#000') : 'transparent',
                }}
              >
                {isCheckAll && <CheckIcon />}
              </span>
              All
            </li>
            {projectProcessFlagList.map((item, idx) => {
              if (item.id === 0) return;
              if (item.id === 4) return;
              if (item.id === -3) return;
              if (item.id === -2 && isClinicUser) return;
              return (
                <li
                  className={cx(`projectList__filter_step_item`, {
                    active: checkedProjectProcess.value.has(item.id),
                    offline: isOffline,
                  })}
                  key={idx}
                  // onClick={() => checkedProjectProcess.onChange({ value: item.id })}
                  onClick={() => !isOffline && onChangeProjectProcess(item.id)}
                >
                  <span
                    className={`projectList__filter_step_check ${item.id}`}
                    style={{
                      borderColor:
                        checkedProjectProcess.value.has(item.id) && !isOffline
                          ? 'transparent'
                          : '#ccc',
                      backgroundColor:
                        checkedProjectProcess.value.has(item.id) && !isOffline
                          ? item.color
                          : 'transparent',
                    }}
                  >
                    {checkedProjectProcess.value.has(item.id) && !isOffline && <CheckIcon />}
                  </span>
                  {t(`${item.code}`)}
                </li>
              );
            })}
          </ul>
        </div>
      </Tooltip>
    </StyledProjectSearchFilter>
  );
};

const StyledProjectSearchFilter = styled.div<{ viewType: string | undefined }>`
  width: 100%;
  & {
    display: flex;
    flex-direction: column;
    row-gap: 25px;
    position: relative;
    z-index: 1;

    .datePicker__box {
      width: 250px;
      .ant-picker {
        border-color: transparent;
      }
    }

    .projectList__filter_step_box {
      display: flex;
      align-items: center;
      padding-left: 5px;
      background-color: #f4f5fa;
      border-radius: 5px;

      .projectList__filter_step_list {
        display: flex;
        align-items: center;
        height: 50px;
        .projectList__filter_step_item {
          display: inline-flex;
          align-items: center;
          margin-right: 40px;
          cursor: default;
          &.offline {
            color: #ccccd4;
          }
          &:first-child {
            margin-left: 40px;
          }
          &:last-child {
          }
          font-size: 13px;
          .projectList__filter_step_check {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            margin-right: 10px;
            width: 20px;
            height: 20px;
            border-radius: 4px;
            background-color: transparent;
            border: 1px solid white;
          }
        }
      }
    }

    .projectList__project_type_select_box {
      margin-left: 15px;
      width: 285px;
    }

    .projectList__search_wrapper {
      position: relative;
      width: ${({ viewType }) => (viewType === 'modal' ? '310px' : '390px')};
      height: 46px;
      .projectList__search_container {
        width: inherit;
        .MuiOutlinedInput-input {
          font-size: 13px;
          font-weight: 400;
        }
        .MuiInputBase-root {
          background-color: #ffffff;
          border: 1px solid #e1e1e1;
          &.Mui-disabled {
            background-color: #f4f4f4;
            .MuiOutlinedInput-notchedOutline {
              border: none;
            }
          }
        }
        .MuiIconButton-edgeEnd {
          margin-right: 30px;
        }
        .projectList__search_btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          position: absolute;
          right: 0;
          top: 0;
          width: 46px;
          height: 46px;
          border-top-right-radius: 5px;
          border-bottom-right-radius: 5px;
          background-color: ${color.navy_blue};
          &.offline {
            background-color: #ccccd4;
          }
        }
      }
    }
  }
`;

export default React.memo(ProjectSearchFilter);
